import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import SlidingSideBar from '../../../SlidingSideBar/SlidingSideBar';
import { Col, FormControl, FormLabel, Row, Card, Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import { ENDPOINTS, ORDER_TYPES, BACKEND_FIELDS_TO_EXCLUDE } from '../../../../helpers/constants';
import { makeRESTApiRequest } from '../../../../helpers/api';
import Loader from '../../../Loader';
import { toast } from 'sonner';
import useReportGeneration from '../../../../hooks/useReportGeneration';
import AlertModal from '../../../AlertModal';
import AdvancedSelect from '../../../common/AdvancedSelect';
import ReportFormGenerator from '../../../reports/FormGenerator';
import StepTabNavigation from '../../../reports/StepTabNavigation';
import ReportActionBar from '../../../reports/ReportActionBar';
import ReportModals from '../../../reports/ReportModals';
import ReportSidebarFooter from '../../../reports/ReportSidebarFooter';
import {
  autoFillInvoicesFromFiles,
  autoFillTaxUtilitiesFromFiles,
  buildFieldStatusMapFromQaIssues,
  buildQaFieldIssueMap,
  buildUnresolvedQaIssueStepKeys,
  createEmptySections,
  fetchReportQaSectionsByOrder,
  handleInvoiceMultiFieldChange,
  getInvoiceMultiFields,
  EMPTY_INVOICE_ROW,
  computeSuggestedInvoiceAmount,
  computeTaxSectionAmountDue,
  isInvoiceTotalBelowSuggested,
  roundMoney,
  resolveQaIssueNavigation,
  resolveReportOrderId,
  findOrderOnService,
  scrollToFieldKey,
  mergeReportAutoOrganizationFiles,
  mergeReportAutoFillFromOrganization
} from '../../../reports/utils/helpers';
import { FieldStatusShell } from '../../../common/fieldStatus';
import { normalizeRichTextValue } from '../../../../helpers/global';
import { toRichTextEditorValue } from '../../../../helpers/safeHtml';
import { noteHasUtilitiesTag } from '../../../../helpers/utilitiesNoteTag';
import FolioInputWithCopy from '../../../common/FolioInputWithCopy';
import WorkflowModal from '../WorkflowModal';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { FaListOl } from 'react-icons/fa6';
import useMunicipalityCounty from '../../../../hooks/useMunicipalityCounty';
import QaAnalysisSideBar from '../../../reports/QaAnalysisSideBar';
import useQuery from '../../../../hooks/useQueryParams';
import UtilitiesProcessorNotes from './UtilitiesProcessorNotes';

const getFormattedContact = (contacts = [], department) => {
  const contact = contacts.find(c =>
    Array.isArray(c.department)
      ? c.department.map(d => d.toLowerCase()).includes(department.toLowerCase())
      : c.department.toLowerCase().includes(department.toLowerCase())
  );

  if (!contact)
    return {
      emails: '',
      numbers: ''
    };

  const { contactEmails = [], contactNumbers = [], city = '', state = '' } = contact;

  return {
    emails: contactEmails.map(e => e.toLowerCase()).join(', '),
    numbers: contactNumbers.join(', '),
    city: city,
    state: state
  };
};

const TAX_DETAIL_KEYS_BY_RADIO = {
  tangibleTax: 'tangibleTaxDetails',
  businessTax: 'businessTaxDetails',
  tangibleDelinquentTax: 'tangibleDelinquentTaxDetails',
  businessDelinquentTax: 'businessDelinquentTaxDetails'
};

const getTaxAccountDetailsField = ({ kind, groupKey, isFilePopupOpen }) => ({
  label: 'Account Details',
  key: `${kind}TaxDetails`,
  type: 'multi-field',
  hideLabel: true,
  cellSize: isFilePopupOpen ? 12 : 6,
  keepColumnPadding: true,
  show: { key: `${kind}Tax`, value: 'Yes' },
  fields: [
    {
      label: 'Account No',
      key: `${kind}TaxAccountNo`,
      type: 'text',
      cellSize: isFilePopupOpen ? 6 : 5
    },
    {
      label: 'Balance',
      key: `${kind}TaxBalance`,
      type: 'currency-input',
      cellSize: isFilePopupOpen ? 5 : 6
    }
  ],
  groupKey
});

const getTaxDelinquentField = ({ kind, groupKey, isFilePopupOpen }) => ({
  label: 'Delinquent Tax',
  key: `${kind}DelinquentTax`,
  type: 'radio-with-additional-fields',
  options: ['No', 'Yes'],
  cellSize: 12,
  card: true,
  show: { key: `${kind}Tax`, value: 'Yes' },
  additionalFields: [
    {
      cellSize: 12,
      columnGroups: [
        {
          fields: [
            {
              label: 'Delinquent Tax Details',
              key: `${kind}DelinquentTaxDetails`,
              type: 'multi-field',
              hideLabel: true,
              cellSize: isFilePopupOpen ? 12 : 6,
              fields: [
                {
                  label: 'Account No',
                  key: `${kind}DelinquentTaxAccountNo`,
                  type: 'text',
                  cellSize: isFilePopupOpen ? 6 : 5
                },
                {
                  label: 'Balance',
                  key: `${kind}DelinquentTaxBalance`,
                  type: 'currency-input',
                  cellSize: isFilePopupOpen ? 5 : 6
                }
              ]
            }
          ]
        }
      ],
      show: { key: `${kind}DelinquentTax`, value: 'Yes' }
    }
  ],
  groupKey
});

const pruneEmptyTaxAccountRows = (taxes, kind) => {
  const detailsKey = `${kind}TaxDetails`;
  const rows = (taxes[detailsKey] || []).filter(row => row?.[`${kind}TaxAccountNo`] || row?.[`${kind}TaxBalance`]);
  if (rows.length) taxes[detailsKey] = rows;
  else delete taxes[detailsKey];
};

const getFormSteps = (isFilePopupOpen, isWorkflowShowing) => [
  {
    key: 'documents',
    name: 'Documents',
    isComplete: false,
    fields: [
      {
        label: 'Invoice Documents',
        key: 'invoicesFiles',
        type: 'file',
        cellSize: isFilePopupOpen ? 12 : 6,
        isInDocumentsTrue: true
      },
      {
        label: 'Tax Documents',
        key: 'taxesFiles',
        type: 'file',
        cellSize: isFilePopupOpen ? 12 : 6,
        isInDocumentsTrue: true
      },
      {
        label: 'Permit Documents',
        key: 'permitsFiles',
        type: 'file',
        cellSize: isFilePopupOpen ? 12 : 6,
        isInDocumentsTrue: true
      },
      {
        label: 'Code Issue Documents',
        key: 'codeIssuesFiles',
        type: 'file',
        cellSize: isFilePopupOpen ? 12 : 6,
        isInDocumentsTrue: true
      },
      {
        label: 'Special Assessment/Lien Documents',
        key: 'specialAssessmentsLiensFiles',
        type: 'file',
        cellSize: isFilePopupOpen ? 12 : 6,
        isInDocumentsTrue: true
      },
      {
        label: 'Utility Documents',
        key: 'utilitiesFiles',
        type: 'file',
        cellSize: isFilePopupOpen ? 12 : 6,
        isInDocumentsTrue: true
      }
    ]
  },
  {
    key: 'taxes',
    name: 'Taxes',
    component: <div>Taxes</div>,
    fields: [
      {
        label: 'Property Taxes',
        type: 'section',
        groupKey: 'propertyTaxes'
      },
      {
        label: 'Current Year',
        key: 'currentYear',
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        type: 'select',
        options: [
          (moment().year() - 1).toString(),
          moment()
            .year()
            .toString(),
          (moment().year() + 1).toString()
        ].map(year => ({
          label: year,
          value: year
        })),
        groupKey: 'propertyTaxes'
      },
      {
        label: 'Gross Amount',
        key: 'grossAmount',
        type: 'currency-input',
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'propertyTaxes'
      },
      {
        label: 'Total Amount Due',
        key: 'totalAmountDue',
        type: 'currency-input',
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'propertyTaxes'
      },
      {
        label: 'Property Tax Status',
        key: 'propertyTaxStatus',
        type: 'radio',
        options: ['Unpaid', 'Paid'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'propertyTaxes',
        required: true,
        disabledOptions: values =>
          values.propertyDelinquentTax === 'Yes' || (values.grossAmount > 0 && values.totalAmountDue > 0)
            ? ['Paid']
            : [],
        disabledToastMessage: values => {
          if (values.propertyDelinquentTax === 'Yes') {
            return {
              Paid: 'Delinquent tax is present; this record can’t be marked Paid.'
            };
          }
          if (values.grossAmount > 0 && values.totalAmountDue > 0) {
            return {
              Paid: 'Total Amount Due is present, this record can’t be marked Paid.'
            };
          }
          return {};
        }
      },

      {
        label: 'Delinquent Tax',
        key: 'propertyDelinquentTax',
        type: 'radio',
        options: ['No', 'Yes'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'propertyTaxes'
      },
      {
        label: 'Year(s) NOT PAID',
        key: 'propertyDelinquentTaxYears',
        type: 'text',
        show: {
          key: 'propertyDelinquentTax',
          value: 'Yes'
        },
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'propertyTaxes'
      },
      {
        label: 'Tangible Taxes',
        type: 'section',
        groupKey: 'tangibleTaxes'
      },
      {
        label: 'Tangible Tax',
        key: 'tangibleTax',
        type: 'radio',
        options: ['No', 'Yes'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'tangibleTaxes'
      },
      {
        label: 'Tangible Tax Status',
        key: 'tangibleTaxStatus',
        type: 'radio',
        options: ['Paid', 'Unpaid'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        show: {
          key: 'tangibleTax',
          value: 'Yes'
        },
        groupKey: 'tangibleTaxes'
      },
      {
        label: 'Total Amount Due',
        key: 'tangibleTaxAmountDue',
        type: 'currency-input',
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        show: [
          {
            key: 'tangibleTax',
            value: 'Yes'
          }
        ],
        groupKey: 'tangibleTaxes'
      },
      getTaxAccountDetailsField({ kind: 'tangible', groupKey: 'tangibleTaxes', isFilePopupOpen }),
      getTaxDelinquentField({ kind: 'tangible', groupKey: 'tangibleTaxes', isFilePopupOpen }),
      {
        label: 'Notes',
        key: 'tangibleTaxNotes',
        type: 'rich-text',
        show: {
          key: 'tangibleTax',
          value: 'Yes'
        },
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        groupKey: 'tangibleTaxes'
      },

      {
        label: 'Business Taxes',
        type: 'section',
        groupKey: 'businessTaxes'
      },
      {
        label: 'Business Tax',
        key: 'businessTax',
        type: 'radio',
        options: ['No', 'Yes'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        groupKey: 'businessTaxes'
      },
      {
        label: 'Business Tax Status',
        key: 'businessTaxStatus',
        type: 'radio',
        options: ['Paid', 'Unpaid'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        show: {
          key: 'businessTax',
          value: 'Yes'
        },
        groupKey: 'businessTaxes'
      },
      {
        label: 'Total Amount Due',
        key: 'businessTaxAmountDue',
        type: 'currency-input',
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 4,
        show: [
          {
            key: 'businessTax',
            value: 'Yes'
          }
        ],
        groupKey: 'businessTaxes'
      },
      getTaxAccountDetailsField({ kind: 'business', groupKey: 'businessTaxes', isFilePopupOpen }),
      getTaxDelinquentField({ kind: 'business', groupKey: 'businessTaxes', isFilePopupOpen }),
      {
        label: 'Notes',
        key: 'businessTaxNotes',
        type: 'rich-text',
        show: {
          key: 'businessTax',
          value: 'Yes'
        },
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        groupKey: 'businessTaxes'
      },
      {
        label: 'Taxes General Notes',
        key: 'taxesNotes',
        type: 'rich-text',
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        groupKey: 'generalInfo'
      },
      {
        label: 'Tax Documents',
        key: 'taxesFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  },
  {
    key: 'permits',
    name: 'Permits',
    component: <div>Permits</div>,
    fields: [
      {
        label: 'Open Permits',
        key: 'openPermits',
        type: 'radio-with-unit-condo-fields',
        options: ['No', 'Yes'],
        cellSize: 12,
        card: true,
        unitKey: 'openPermitsUnit',
        condoKey: 'openPermitsCondo',
        unitNumbersKey: 'openPermitsUnitNumbers',
        condoNumbersKey: 'openPermitsCondoNumbers',
        condoInputTypeKey: 'openPermitsCondoInputType',
        fields: [
          {
            label: 'Permit Number',
            key: 'number',
            type: 'text',
            cellSize: 11
          }
        ]
      },
      {
        label: 'Expired Permits',
        key: 'expiredPermits',
        type: 'radio-with-unit-condo-fields',
        options: ['No', 'Yes'],
        cellSize: 12,
        card: true,
        unitKey: 'expiredPermitsUnit',
        condoKey: 'expiredPermitsCondo',
        unitNumbersKey: 'expiredPermitsUnitNumbers',
        condoNumbersKey: 'expiredPermitsCondoNumbers',
        condoInputTypeKey: 'expiredPermitsCondoInputType',
        fields: [
          {
            label: 'Permit Number',
            key: 'number',
            type: 'text',
            cellSize: 11
          }
        ]
      },
      {
        label: 'Building Violations',
        key: 'buildingViolations',
        type: 'radio-with-unit-condo-fields',
        options: ['No', 'Yes'],
        cellSize: 12,
        card: true,
        unitKey: 'buildingViolationsUnit',
        condoKey: 'buildingViolationsCondo',
        unitNumbersKey: 'buildingViolationsUnitNumbers',
        condoNumbersKey: 'buildingViolationsCondoNumbers',
        condoInputTypeKey: 'buildingViolationsCondoInputType',
        fields: [
          {
            label: 'Permit Number',
            key: 'number',
            type: 'text',
            cellSize: 11
          }
        ]
      },
      {
        type: 'offset',
        cellSize: 12
      },
      {
        label: 'City Contact Email',
        key: 'permitContactEmail',
        type: 'text',
        cellSize: 6,
        groupKey: 'permitsContactInfo'
      },
      {
        label: 'City Contact Number',
        key: 'permitContactNumber',
        type: 'text',
        cellSize: 6,
        groupKey: 'permitsContactInfo'
      },
      {
        label: 'County Contact Email',
        key: 'permitCountyContactEmail',
        type: 'text',
        cellSize: 6,
        groupKey: 'permitsContactInfo'
      },
      {
        label: 'County Contact Number',
        key: 'permitCountyContactNumber',
        type: 'text',
        cellSize: 6,
        groupKey: 'permitsContactInfo'
      },
      {
        label: 'Permit Documents',
        key: 'permitsFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  },
  {
    key: 'codeIssues',
    name: 'Code Issues',
    component: <div>Code Issues</div>,
    fields: [
      {
        label: 'Code Issues',
        key: 'pendingViolations',
        type: 'radio-with-unit-condo-fields',
        options: ['No', 'Yes'],
        cellSize: 12,
        card: true,
        unitKey: 'pendingViolationsUnit',
        condoKey: 'pendingViolationsCondo',
        unitNumbersKey: 'pendingViolationsUnitCases',
        condoNumbersKey: 'pendingViolationsCondoCases',
        condoInputTypeKey: 'pendingViolationsCondoInputType',
        fields: [
          {
            label: 'Reference',
            key: 'Reference',
            type: 'text',
            cellSize: isFilePopupOpen ? 6 : 5
          },
          {
            label: 'Due',
            key: 'due',
            type: 'currency-input',
            cellSize: isFilePopupOpen ? 5 : 6
          }
        ]
      },
      {
        key: 'openInvoices',
        type: 'open-invoices-input',
        cellSize: 12
      },
      {
        label: 'Notes',
        key: 'violationNotes',
        type: 'rich-text',
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        groupKey: 'codeIssuesContactInfo'
      },
      {
        label: 'City Contact Email',
        key: 'violationContactEmail',
        type: 'text',
        cellSize: 6,
        groupKey: 'codeIssuesContactInfo'
      },
      {
        label: 'City Contact Number',
        key: 'violationContactNumber',
        type: 'text',
        cellSize: 6,
        groupKey: 'codeIssuesContactInfo'
      },
      {
        label: 'County Contact Email',
        key: 'violationCountyContactEmail',
        type: 'text',
        cellSize: 6,
        groupKey: 'codeIssuesContactInfo'
      },
      {
        label: 'County Contact Number',
        key: 'violationCountyContactNumber',
        type: 'text',
        cellSize: 6,
        groupKey: 'codeIssuesContactInfo'
      },
      {
        label: 'Code Issue Documents',
        key: 'codeIssuesFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  },
  {
    key: 'specialAssessmentsLiens',
    name: 'Special Assessments/Liens',
    fields: [
      {
        label: 'Special Assessments',
        key: 'specialAssessments',
        type: 'radio',
        options: ['No', 'Yes'],
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 3,
        groupKey: 'specialAssessments'
      },
      {
        label: 'Collected Through Taxes',
        key: 'specialAssessmentsCollected',
        type: 'radio',
        options: ['No', 'Yes'],
        show: {
          key: 'specialAssessments',
          value: 'Yes'
        },
        cellSize: isWorkflowShowing && isFilePopupOpen ? 12 : 9,
        groupKey: 'specialAssessments'
      },
      {
        label: 'Notes',
        key: 'specialAssessmentsNotes',
        type: 'rich-text',
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        groupKey: 'specialAssessments'
      },
      {
        label: 'Liens',
        key: 'liens',
        type: 'radio-with-unit-condo-fields',
        options: ['No', 'Yes'],
        cellSize: 12,
        className: 'mb-2',
        groupKey: 'specialAssessmentsLiens',
        unitKey: 'liensUnit',
        condoKey: 'liensCondo',
        unitNumbersKey: 'liensUnitNumbers',
        condoNumbersKey: 'liensCondoNumbers',
        condoInputTypeKey: 'liensCondoInputType',
        fields: [
          {
            label: 'Lien Number',
            key: 'number',
            type: 'text',
            cellSize: 11
          }
        ]
      },
      {
        label: 'Notes',
        key: 'liensNotes',
        type: 'rich-text',
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        show: {
          key: 'liens',
          value: 'Yes'
        },
        groupKey: 'specialAssessmentsLiens'
      },
      {
        label: 'Special Assessment/Lien Documents',
        key: 'specialAssessmentsLiensFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  },
  {
    key: 'utilities',
    name: 'Utilities',
    fields: [
      {
        label: 'Utility Liens',
        key: 'utilitiesLiens',
        type: 'radio',
        groupKey: 'utilityLiens',
        options: ['No', 'Yes'],
        cellSize: 12
      },
      {
        label: 'Services',
        key: 'services',
        type: 'utility-services-table-input',
        cellSize: 12,
        groupKey: 'utilitiesAmountPending'
      },
      {
        label: 'Notes',
        key: 'utilitiesLiensNotes',
        type: 'rich-text',
        groupKey: 'utilityLiens',
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        show: {
          key: 'utilitiesLiens',
          value: 'Yes'
        }
      },
      {
        label: 'Utilities General Notes',
        key: 'utilitiesNotes',
        type: 'rich-text',
        cellSize: 12,
        height: '160px',
        enableLists: true,
        placeholder: 'Enter notes...',
        groupKey: 'generalInfo'
      },
      {
        label: 'Utility Documents',
        key: 'utilitiesFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  },
  {
    key: 'invoices',
    name: 'Invoices',
    isComplete: false,
    component: <div>Invoices</div>,
    cellSize: 12,
    fields: [
      {
        label: 'Fees',
        hideLabel: true,
        type: 'fixed-fields',
        cellSize: 12,
        groupKey: 'invoices',
        fields: [
          {
            label: 'Hard Cost',
            key: 'hardCost',
            hideLabel: true,
            type: 'currency-input',
            cellSize: 12,
            cellSizeMd: 4,
            cellSizeLg: isFilePopupOpen ? 4 : 2,
            groupKey: 'invoices'
          },

          {
            label: 'Processing Fee',
            key: 'lienSearchProcessingFee',
            hideLabel: true,
            type: 'currency-input',
            cellSize: 12,
            cellSizeMd: 4,
            cellSizeLg: isFilePopupOpen ? 4 : 2,
            groupKey: 'invoices'
          },
          {
            label: 'Suggested Invoice Amount',
            key: 'suggestedInvoiceAmount',
            type: 'static',
            displayAs: 'currency',
            note: values => {
              const min = Number(values?.minimumInvoicableAmount) || 0;
              const formatted = `$${min.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
              return `Note: Minimum invoicable amount is ${formatted}.`;
            },
            staticTone: values => (isInvoiceTotalBelowSuggested(values) ? 'error' : 'default'),
            staticTooltip: values => {
              if (!isInvoiceTotalBelowSuggested(values)) return null;
              const suggested = Number(values?.suggestedInvoiceAmount) || 0;
              const totalInvoiceAmount = (values?.invoices || []).reduce((sum, row) => {
                return sum + (Number(row?.invoiceAmount) || 0);
              }, 0);

              return `Suggested invoice amount ($${suggested}) is less than total invoice amount ($${totalInvoiceAmount}).`;
            },
            showStaticIcon: values => isInvoiceTotalBelowSuggested(values),
            cellSize: 12,
            cellSizeMd: 4,
            cellSizeLg: isFilePopupOpen ? 4 : 2,
            groupKey: 'invoices'
          }
        ]
      },
      {
        label: 'Invoices',
        key: 'invoices',
        hideLabel: true,
        type: 'multi-field',
        cellSize: 12,
        groupKey: 'invoices',
        className: 'px-3 mt-3',
        ...(isFilePopupOpen ? { removeButtonPosition: 'top-right' } : {}),
        fields: getInvoiceMultiFields({ isFilePopupOpen })
      },
      {
        label: 'Invoice Documents',
        key: 'invoicesFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  }
];

const defaultValues = {
  invoices: {
    invoices: [{ ...EMPTY_INVOICE_ROW }],
    hardCost: null,
    lienSearchProcessingFee: null,
    minimumInvoicableAmount: null,
    suggestedInvoiceAmount: null
  },
  taxes: {
    currentYear: (moment().year() - 1).toString(),
    propertyTaxStatus: undefined,
    propertyDelinquentTax: 'No',
    tangibleTax: 'No',
    tangibleTaxStatus: undefined,
    tangibleTaxAmountDue: '',
    tangibleTaxDetails: [{ tangibleTaxAccountNo: '', tangibleTaxBalance: '' }],
    tangibleDelinquentTax: 'No',
    tangibleDelinquentTaxDetails: [{ tangibleDelinquentTaxAccountNo: '', tangibleDelinquentTaxBalance: '' }],
    businessTax: 'No',
    businessTaxStatus: undefined,
    businessTaxAmountDue: '',
    businessTaxDetails: [{ businessTaxAccountNo: '', businessTaxBalance: '' }],
    businessDelinquentTax: 'No',
    businessDelinquentTaxDetails: [{ businessDelinquentTaxAccountNo: '', businessDelinquentTaxBalance: '' }],
    taxesNotes: '',
    files: []
  },
  permits: {
    openPermits: 'No',
    openPermitsUnit: false,
    openPermitsCondo: false,
    openPermitsUnitNumbers: [{ number: '' }],
    openPermitsCondoNumbers: [{ number: '' }],
    openPermitsCondoInputType: 'Numbers',
    openPermitsCondoFreeText: '',
    expiredPermits: 'No',
    expiredPermitsUnit: false,
    expiredPermitsCondo: false,
    expiredPermitsUnitNumbers: [{ number: '' }],
    expiredPermitsCondoNumbers: [{ number: '' }],
    expiredPermitsCondoInputType: 'Numbers',
    expiredPermitsCondoFreeText: '',
    buildingViolations: 'No',
    buildingViolationsUnit: false,
    buildingViolationsCondo: false,
    buildingViolationsUnitNumbers: [{ number: '' }],
    buildingViolationsCondoNumbers: [{ number: '' }],
    buildingViolationsCondoInputType: 'Numbers',
    buildingViolationsCondoFreeText: '',
    files: []
  },
  codeIssues: {
    pendingViolations: 'No',
    pendingViolationsUnit: false,
    pendingViolationsCondo: false,
    pendingViolationsUnitCases: [{ Reference: '', due: '' }],
    pendingViolationsCondoCases: [{ Reference: '', due: '' }],
    openInvoices: 'No',
    openInvoicesUnit: false,
    openInvoicesCondo: false,
    openInvoicesUnitCases: [{ description: '', amount: '' }],
    openInvoicesCondoCases: [{ description: '', amount: '' }],
    files: []
  },
  specialAssessmentsLiens: {
    specialAssessments: 'No',
    specialAssessmentsCollected: 'No',
    liens: 'No',
    files: []
  },
  utilities: {
    providerName: '',
    providerType: 'Individual',
    utilitiesAmountPending: 'No',
    utilitiesLiens: 'No',
    utilityContact: '',
    wasteProviderName: '',
    wasteProviderType: 'Individual',
    accountNo: '',
    files: []
  },
  documents: {
    isComplete: false,
    invoicesFiles: [],
    taxesFiles: [],
    permitsFiles: [],
    codeIssuesFiles: [],
    specialAssessmentsLiensFiles: [],
    utilitiesFiles: []
  }
};

const fieldsToIncludeInFileInformation = ['propertyAddress', 'fileNo', 'folio', 'municipality', 'owner'];

const isBlankAmount = amount => amount === '' || amount == null;

// Open invoice rows to save: only when Yes, Unit rows first, completely empty rows dropped.
const getOpenInvoiceRows = section => {
  if (section?.openInvoices !== 'Yes') return [];
  const toRows = (flag, rows, type) =>
    section[flag]
      ? (rows || [])
          .filter(row => row.description?.trim() || !isBlankAmount(row.amount))
          .map(row => ({
            description: row.description?.trim() || '',
            amount: row.amount ?? '',
            openInvoicesFor: type
          }))
      : [];
  return [
    ...toRows('openInvoicesUnit', section.openInvoicesUnitCases, 'Unit'),
    ...toRows('openInvoicesCondo', section.openInvoicesCondoCases, 'Condo')
  ];
};

const hydrateInvoiceFees = (invoices = {}, lienSearchData) => {
  const root = lienSearchData?.[0] || lienSearchData || {};
  const lienOrder =
    (root?.orders || []).find(
      order => order?.orderType === ORDER_TYPES.LIEN_SEARCH || order?.orderType === 'Lien Search'
    ) || {};
  const organization = root?.organization || {};

  const hardCost = lienOrder.hardCost ?? invoices.hardCost ?? 0;
  const lienSearchProcessingFee =
    lienOrder.processingFee ?? organization.lienSearchProcessingFee ?? invoices.lienSearchProcessingFee ?? 0;
  const minimumInvoicableAmount =
    organization.lienSearchMinimumInvoicableAmount ?? invoices.minimumInvoicableAmount ?? 0;

  return {
    ...invoices,
    hardCost,
    lienSearchProcessingFee,
    minimumInvoicableAmount,
    suggestedInvoiceAmount: computeSuggestedInvoiceAmount(hardCost, lienSearchProcessingFee, minimumInvoicableAmount)
  };
};

const REPORT_RICH_TEXT_FIELDS = {
  taxes: ['tangibleTaxNotes', 'businessTaxNotes', 'taxesNotes'],
  codeIssues: ['violationNotes'],
  specialAssessmentsLiens: ['specialAssessmentsNotes', 'liensNotes'],
  utilities: ['utilitiesLiensNotes', 'utilitiesNotes']
};

/** Legacy notes are plain text. Quill needs each line wrapped and escaped before it mounts. */
const coerceReportRichTextFields = sections => {
  Object.entries(REPORT_RICH_TEXT_FIELDS).forEach(([section, keys]) => {
    const sectionValues = sections?.[section];
    if (!sectionValues) return;
    keys.forEach(key => {
      const value = sectionValues[key];
      if (typeof value !== 'string' || !value) return;
      sectionValues[key] = toRichTextEditorValue(value);
    });
  });
};

/** Maps API report document to sidebar form state (same pipeline as loading from lienSearch.report). */
const buildFormStateFromReportDocument = (report, lienSearchData = null) => {
  if (!report) return null;

  const reportValues = Object.fromEntries(
    Object.entries(report).filter(([key]) => !fieldsToIncludeInFileInformation.includes(key))
  );

  // Deep merge defaultValues with reportValues.
  // Only form section keys — skip report metadata/arrays like pdfHighlights, generatedFiles.
  // Spreading an array into an object turns [] into {} and breaks Mongoose on save/preview.
  const mergedValues = { ...defaultValues };
  Object.keys(reportValues).forEach(key => {
    if (!(key in defaultValues)) return;
    const value = reportValues[key];
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      mergedValues[key] = { ...defaultValues[key], ...value };
    } else if (!Array.isArray(value)) {
      mergedValues[key] = value;
    }
  });

  // Transform permit and code issue data from backend format to frontend format
  const transformSectionData = (section, config) => {
    if (!mergedValues[section]) return;

    const hasBackendData = config.some(({ infoField }) => mergedValues[section][infoField]);

    // Check if we have backend data to load
    if (hasBackendData) {
      config.forEach(({ type, infoField, forField, numbersField, defaultItem }) => {
        const unitItems = [];
        const condoItems = [];
        let hasCondoData = false;

        if (mergedValues[section][infoField]) {
          mergedValues[section][infoField].forEach(item => {
            if (item[forField] === 'Unit' && (numbersField ? item[numbersField] : item.Reference || item.due)) {
              unitItems.push(
                numbersField ? { number: item[numbersField] } : { Reference: item.Reference || '', due: item.due || '' }
              );
            } else if (item[forField] === 'Condo') {
              hasCondoData = true; // Mark that we have condo datas
              if (item.inputType === 'freeText') {
                mergedValues[section][`${type}CondoInputType`] = 'Free Text';
                mergedValues[section][`${type}CondoFreeText`] = numbersField
                  ? item[numbersField]
                  : item.Reference || '';
              } else if (numbersField ? item[numbersField] : item.Reference || item.due) {
                condoItems.push(
                  numbersField
                    ? { number: item[numbersField] }
                    : { Reference: item.Reference || '', due: item.due || '' }
                );
              }
            }
          });
        }

        mergedValues[section][`${type}Unit`] = unitItems.length > 0;
        mergedValues[section][`${type}Condo`] = hasCondoData;
        mergedValues[section][`${type}Unit${numbersField ? 'Numbers' : 'Cases'}`] =
          unitItems.length > 0 ? unitItems : [defaultItem];
        mergedValues[section][`${type}Condo${numbersField ? 'Numbers' : 'Cases'}`] =
          condoItems.length > 0 ? condoItems : [defaultItem];
      });
    }
  };

  // Transform permits data
  transformSectionData('permits', [
    {
      type: 'openPermits',
      infoField: 'openPermitsInfo',
      forField: 'openPermitsFor',
      numbersField: 'openPermitsPermitNumbers',
      defaultItem: { number: '' }
    },
    {
      type: 'expiredPermits',
      infoField: 'expiredPermitsInfo',
      forField: 'expiredPermitsFor',
      numbersField: 'expiredPermitsPermitNumbers',
      defaultItem: { number: '' }
    },
    {
      type: 'buildingViolations',
      infoField: 'buildingViolationsInfo',
      forField: 'buildingViolationsFor',
      numbersField: 'buildingViolationsNumbers',
      defaultItem: { number: '' }
    }
  ]);

  // Transform code issues data
  transformSectionData('codeIssues', [
    {
      type: 'pendingViolations',
      infoField: 'pendingViolationsCases',
      forField: 'pendingViolationsFor',
      defaultItem: { Reference: '', due: '' }
    }
  ]);

  // Split saved open invoice rows back into the Unit / Condo panels
  const savedInvoices = mergedValues.codeIssues?.openInvoicesCases;
  if (Array.isArray(savedInvoices)) {
    const toRow = ({ description, amount }) => ({ description: description || '', amount: amount || '' });
    const unitRows = savedInvoices.filter(i => i.openInvoicesFor === 'Unit').map(toRow);
    const condoRows = savedInvoices.filter(i => i.openInvoicesFor === 'Condo').map(toRow);
    mergedValues.codeIssues.openInvoicesUnit = unitRows.length > 0;
    mergedValues.codeIssues.openInvoicesCondo = condoRows.length > 0;
    mergedValues.codeIssues.openInvoicesUnitCases = unitRows.length > 0 ? unitRows : [{ description: '', amount: '' }];
    mergedValues.codeIssues.openInvoicesCondoCases =
      condoRows.length > 0 ? condoRows : [{ description: '', amount: '' }];
  }

  // Transform special assessments/liens data
  transformSectionData('specialAssessmentsLiens', [
    {
      type: 'liens',
      infoField: 'liensInfo',
      forField: 'liensFor',
      numbersField: 'liensNumbers',
      defaultItem: { number: '' }
    }
  ]);

  // Map files from each section to documents tab
  const documentsMapping = {
    invoicesFiles: reportValues?.invoices?.files || [],
    taxesFiles: reportValues?.taxes?.files || [],
    permitsFiles: reportValues?.permits?.files || [],
    codeIssuesFiles: reportValues?.codeIssues?.files || [],
    specialAssessmentsLiensFiles: reportValues?.specialAssessmentsLiens?.files || [],
    utilitiesFiles: reportValues?.utilities?.files || []
  };

  delete mergedValues.isDocumentSectionComplete;
  mergedValues.documents = {
    ...defaultValues.documents,
    ...documentsMapping,
    isComplete: !!report.isDocumentSectionComplete
  };
  coerceReportRichTextFields(mergedValues);
  mergedValues.invoices = hydrateInvoiceFees(mergedValues.invoices, lienSearchData);
  if (Array.isArray(mergedValues.invoices?.invoices)) {
    mergedValues.invoices = {
      ...mergedValues.invoices,
      invoices: mergedValues.invoices.invoices.map(inv => ({
        ...(inv._id != null && { _id: String(inv._id) }),
        ...(inv.serviceVersionId != null && {
          serviceVersionId: String(inv.serviceVersionId)
        }),
        invoiceNumber: inv.invoiceNumber ?? '',
        invoiceAmount: inv.invoiceAmount ?? '',
        paymentReceived: inv.paymentReceived ?? '',
        invoiceDueDate: inv.invoiceDueDate ?? '',
        status: inv.status || 'Unpaid'
      }))
    };
  }

  const { propertyAddress, fileNo, folio, municipality, owner } = report;
  return {
    mergedValues,
    fileInformation: { propertyAddress, fileNo, folio, municipality, owner }
  };
};

const REPORT_STEP_TO_INSTRUCTION_TAB = {
  taxes: 'Taxes',
  permits: 'Permits',
  codeIssues: 'Code',
  specialAssessmentsLiens: 'Liens',
  utilities: 'Utilities'
};

const AddEditReportSideBar = ({ show, onHide, lienSearch, loading, service, orderType = null, onAfterSave }) => {
  const { openQa } = useQuery();
  const [step, setStep] = useState(0);
  const { municipalityOptions } = useMunicipalityCounty({ isEnabled: show });
  const [driveFiles, setDriveFiles] = useState(null);
  const [driveFilesLoading, setDriveFilesLoading] = useState(false);
  const [isWorkflowVisible, setIsWorkflowVisible] = useState(false);
  const [workflowMeta, setWorkflowMeta] = useState({
    show: false
  });
  const [formValues, setFormValues] = useState(() => {
    const built = buildFormStateFromReportDocument(lienSearch?.report, lienSearch);
    return built?.mergedValues || { ...defaultValues };
  });

  const [fileInformation, setFileInformation] = useState({
    propertyAddress: '',
    fileNo: '',
    folio: '',
    municipality: '',
    owner: ''
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [fileShareMetadata, setFileShareMetadata] = useState(null);
  const [historyReport, setHistoryReport] = useState([]);
  const [municipalityMeta, setMunicipalityMeta] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [qaAnalysisMeta, setQaAnalysisMeta] = useState({
    show: false
  });
  const [qaFieldIssues, setQaFieldIssues] = useState({});
  const [qaSections, setQaSections] = useState(() => createEmptySections());
  const [qaDocumentId, setQaDocumentId] = useState(null);
  const [activeQaIssueId, setActiveQaIssueId] = useState(null);
  const [pendingQaScrollField, setPendingQaScrollField] = useState(null);
  const [autoOrganizationRunning, setAutoOrganizationRunning] = useState(
    () => !!lienSearch?.report?.autoOrganizationRunning
  );

  // Track unsaved changes
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [initialFormState, setInitialFormState] = useState(null);

  const onPreviewHistory = () => {
    setShowHistory(true);
  };

  const onCloseHistory = () => {
    setShowHistory(false);
  };

  const onFileShareClick = () => {
    setFileShareMetadata({});
  };

  // Helper function to remove backend-specific fields
  const cleanFormStateForComparison = state => {
    const cleaned = JSON.parse(JSON.stringify(state));

    BACKEND_FIELDS_TO_EXCLUDE.forEach(field => {
      delete cleaned[field];
    });

    return cleaned;
  };

  // Helper function to normalize fileInformation for comparison
  const normalizeFileInformation = fileInfo => {
    const { propertyAddress, fileNo, folio, municipality, owner } = fileInfo;
    return { propertyAddress, fileNo, folio, municipality, owner };
  };

  // Helper function to update initial state after save/preview
  const updateInitialFormState = () => {
    const cleanedFormValues = cleanFormStateForComparison(formValues);
    const normalizedFileInfo = normalizeFileInformation(fileInformation);
    setInitialFormState({
      formValues: cleanedFormValues,
      fileInformation: normalizedFileInfo
    });
  };

  const onReportGenerationSuccess = savedReport => {
    if (savedReport) {
      const built = buildFormStateFromReportDocument(savedReport, null);
      if (built) {
        setFormValues(built.mergedValues);
        setFileInformation(built.fileInformation);

        // initial form state for unsaved changes tracking
        const cleanedFormValues = cleanFormStateForComparison(built.mergedValues);
        setInitialFormState({
          formValues: cleanedFormValues,
          fileInformation: normalizeFileInformation(built.fileInformation)
        });
      }
    } else {
      updateInitialFormState();
    }
    onAfterSave?.();
  };

  // check if there are unsaved changes
  const checkForUnsavedChanges = () => {
    if (!initialFormState) return false;

    const cleanedCurrentFormValues = cleanFormStateForComparison(formValues);
    const normalizedFileInfo = normalizeFileInformation(fileInformation);
    const currentState = { formValues: cleanedCurrentFormValues, fileInformation: normalizedFileInfo };

    return !isEqual(currentState, initialFormState);
  };

  const hasUnsavedChanges = useMemo(() => checkForUnsavedChanges(), [formValues, fileInformation, initialFormState]);

  // close with unsaved changes check
  const handleClose = () => {
    if (checkForUnsavedChanges()) {
      setShowUnsavedChangesAlert(true);
    } else {
      onHide();
    }
  };

  // save unsaved changes and continue
  const handleSaveAndContinue = async () => {
    const saveResult = await onSaveReport();

    // save successful
    if (saveResult === true) {
      setShowUnsavedChangesAlert(false);
      onHide();
    } else {
      // save failed
      setShowUnsavedChangesAlert(false);
    }
  };

  // discard unsaved changes and continue
  const handleDiscardChanges = () => {
    setShowUnsavedChangesAlert(false);
    onHide();
  };

  // cancel unsaved changes and continue
  const handleCancelAction = () => {
    setShowUnsavedChangesAlert(false);
  };

  // toggle step completion
  const handleToggleStepCompletion = (sectionKey, isComplete) => {
    // Property Tax Status is required before marking the Taxes tab as complete
    if (sectionKey === 'taxes' && isComplete) {
      if (!formValues?.taxes?.propertyTaxStatus) {
        toast.warning('Property Tax Status is required.');
        return;
      }
    }

    setFormValues(prev => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        isComplete: isComplete
      }
    }));
  };

  const loadDriveFiles = async folderId => {
    setDriveFilesLoading(true);
    const { response, error } = await makeRESTApiRequest({
      method: 'GET',
      endpoint: `${ENDPOINTS.GOOGLE_DRIVE_READ_FOLDER}?folderId=${folderId}`
    });

    setDriveFilesLoading(false);

    if (error) {
      toast.error('Failed to load drive files');
      return;
    }

    setDriveFiles(response);
  };

  useEffect(() => {
    if (lienSearch?.report) {
      const built = buildFormStateFromReportDocument(lienSearch.report, lienSearch);
      if (!built) return;

      setFormValues(built.mergedValues);
      setFileInformation(built.fileInformation);

      const cleanedFormValues = cleanFormStateForComparison(built.mergedValues);
      setInitialFormState({
        formValues: cleanedFormValues,
        fileInformation: normalizeFileInformation(built.fileInformation)
      });
    }
  }, [lienSearch]);

  const applyAutoOrganizationReport = useCallback((report, { includeAutoFill = false } = {}) => {
    const merge = includeAutoFill ? mergeReportAutoFillFromOrganization : mergeReportAutoOrganizationFiles;
    setFormValues(prev => merge(prev, report));
    setInitialFormState(prev => {
      if (!prev?.formValues) return prev;
      return { ...prev, formValues: merge(prev.formValues, report) };
    });
  }, []);

  const reportDocumentId = lienSearch?.reportId || lienSearch?.report?._id;
  const parentAutoOrganizationRunning = !!lienSearch?.report?.autoOrganizationRunning;
  const autoOrganizationRunningRef = useRef(autoOrganizationRunning);
  autoOrganizationRunningRef.current = autoOrganizationRunning;

  useEffect(() => {
    setAutoOrganizationRunning(parentAutoOrganizationRunning);
  }, [parentAutoOrganizationRunning]);

  useEffect(() => {
    if (!show || !reportDocumentId) return undefined;

    let cancelled = false;
    let timeoutId;
    let seenRunning = parentAutoOrganizationRunning;
    let finished = false;
    const startedAt = Date.now();
    const POLL_MS = 3000;
    const INITIAL_WINDOW_MS = 15000;

    const poll = async () => {
      const { response } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.REPORT_WITH_ID(reportDocumentId),
        method: 'GET'
      });
      if (cancelled || !response) return;

      const running = !!response.autoOrganizationRunning;
      setAutoOrganizationRunning(running);
      autoOrganizationRunningRef.current = running;

      if (running) {
        seenRunning = true;
        applyAutoOrganizationReport(response, { includeAutoFill: false });
        return;
      }

      if (seenRunning) {
        applyAutoOrganizationReport(response, { includeAutoFill: true });
        seenRunning = false;
        finished = true;
        toast.success('Documents organized into report tabs');
      }
    };

    const tick = async () => {
      await poll();
      if (cancelled || finished) return;
      if (seenRunning || autoOrganizationRunningRef.current || Date.now() - startedAt < INITIAL_WINDOW_MS) {
        timeoutId = setTimeout(tick, POLL_MS);
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [show, reportDocumentId, applyAutoOrganizationReport, parentAutoOrganizationRunning]);

  // browser refresh/close unsaved changes alert
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (show && checkForUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [show, formValues, fileInformation, initialFormState]);

  // provider required for individual account type
  const validateUtilities = () => {
    if (formValues?.utilities?.services?.length) {
      const isAnyProviderMissing = formValues.utilities.services.some(
        svc => svc.accountType === 'Individual' && !svc.provider
      );

      if (isAnyProviderMissing) {
        setShowValidationErrors(true);
        toast.warning('Please complete the required fields');
        return false;
      }
      setShowValidationErrors(false);
    }
    return true;
  };

  const onTabSelect = key => {
    const currentStep = formSteps[step];
    if (currentStep.key === 'utilities') {
      if (!validateUtilities()) return;
    }
    const index = formSteps.findIndex(s => s.key === key);
    if (index !== -1) setStep(index);
  };

  const prepareReportData = () => {
    // Create a deep copy to avoid mutating the original formValues
    const reportData = JSON.parse(JSON.stringify(formValues));
    Object.entries(REPORT_RICH_TEXT_FIELDS).forEach(([section, keys]) => {
      if (!reportData[section]) return;
      keys.forEach(key => {
        reportData[section][key] = normalizeRichTextValue(reportData[section][key]);
      });
    });

    // Helper function to transform permit-like data
    const transformPermitData = (sectionData, fieldPrefix, targetArray, itemTransformer) => {
      if (sectionData[fieldPrefix] === 'Yes') {
        // Process Unit data
        if (sectionData[`${fieldPrefix}Unit`] && sectionData[`${fieldPrefix}UnitNumbers`]) {
          sectionData[`${fieldPrefix}UnitNumbers`].forEach(item => {
            if (item.number && item.number.trim()) {
              targetArray.push(itemTransformer(item.number.trim(), 'Unit', 'numbers'));
            }
          });
        }

        // Process Condo data
        if (sectionData[`${fieldPrefix}Condo`]) {
          if (
            sectionData[`${fieldPrefix}CondoInputType`] === 'Free Text' &&
            sectionData[`${fieldPrefix}CondoFreeText`]
          ) {
            targetArray.push(itemTransformer(sectionData[`${fieldPrefix}CondoFreeText`], 'Condo', 'freeText'));
          } else if (sectionData[`${fieldPrefix}CondoNumbers`]) {
            sectionData[`${fieldPrefix}CondoNumbers`].forEach(item => {
              if (item.number && item.number.trim()) {
                targetArray.push(itemTransformer(item.number.trim(), 'Condo', 'numbers'));
              }
            });
          }
        }
      }
    };

    // Helper function to transform structured data (like code issues)
    const transformStructuredData = (sectionData, fieldPrefix, targetArray, itemTransformer) => {
      if (sectionData[fieldPrefix] === 'Yes') {
        // Process Unit data
        if (sectionData[`${fieldPrefix}Unit`] && sectionData[`${fieldPrefix}UnitCases`]) {
          sectionData[`${fieldPrefix}UnitCases`].forEach(item => {
            if (item.Reference || item.due) {
              targetArray.push(itemTransformer(item, 'Unit', 'numbers'));
            }
          });
        }

        // Process Condo data
        if (sectionData[`${fieldPrefix}Condo`]) {
          if (
            sectionData[`${fieldPrefix}CondoInputType`] === 'Free Text' &&
            sectionData[`${fieldPrefix}CondoFreeText`]
          ) {
            targetArray.push(
              itemTransformer({ Reference: sectionData[`${fieldPrefix}CondoFreeText`], due: '' }, 'Condo', 'freeText')
            );
          } else if (
            sectionData[`${fieldPrefix}CondoInputType`] !== 'Free Text' &&
            sectionData[`${fieldPrefix}CondoCases`]
          ) {
            sectionData[`${fieldPrefix}CondoCases`].forEach(item => {
              if (item.Reference || item.due) {
                targetArray.push(itemTransformer(item, 'Condo', 'numbers'));
              }
            });
          }
        }
      }
    };

    // Transform permits data
    if (reportData.permits) {
      // Transform open permits
      const openPermitsInfo = [];
      transformPermitData(reportData.permits, 'openPermits', openPermitsInfo, (value, type, inputType) => ({
        openPermitsPermitNumbers: value,
        openPermitsFor: type,
        inputType
      }));
      reportData.permits.openPermitsInfo = openPermitsInfo;

      // Transform expired permits
      const expiredPermitsInfo = [];
      transformPermitData(reportData.permits, 'expiredPermits', expiredPermitsInfo, (value, type, inputType) => ({
        expiredPermitsPermitNumbers: value,
        expiredPermitsFor: type,
        inputType
      }));
      reportData.permits.expiredPermitsInfo = expiredPermitsInfo;

      // Transform building violations
      const buildingViolationsInfo = [];
      transformPermitData(
        reportData.permits,
        'buildingViolations',
        buildingViolationsInfo,
        (value, type, inputType) => ({
          buildingViolationsNumbers: value,
          buildingViolationsFor: type,
          inputType
        })
      );
      reportData.permits.buildingViolationsInfo = buildingViolationsInfo;
    }

    // Transform code issues data
    if (reportData.codeIssues) {
      const pendingViolationsCases = [];
      transformStructuredData(
        reportData.codeIssues,
        'pendingViolations',
        pendingViolationsCases,
        (item, type, inputType) => ({
          Reference: item.Reference || '',
          due: item.due || '',
          pendingViolationsFor: type,
          inputType
        })
      );
      reportData.codeIssues.pendingViolationsCases = pendingViolationsCases;
      reportData.codeIssues.openInvoicesCases = getOpenInvoiceRows(reportData.codeIssues);
    }

    // Transform special assessments/liens data
    if (reportData.specialAssessmentsLiens) {
      const liensInfo = [];
      transformPermitData(reportData.specialAssessmentsLiens, 'liens', liensInfo, (value, type, inputType) => ({
        liensNumbers: value,
        liensFor: type,
        inputType
      }));
      reportData.specialAssessmentsLiens.liensInfo = liensInfo;
    }

    // Clean up frontend fields using a mapping approach
    const cleanupFields = {
      permits: [
        'openPermitsUnit',
        'openPermitsCondo',
        'openPermitsUnitNumbers',
        'openPermitsCondoNumbers',
        'openPermitsCondoInputType',
        'openPermitsCondoFreeText',
        'expiredPermitsUnit',
        'expiredPermitsCondo',
        'expiredPermitsUnitNumbers',
        'expiredPermitsCondoNumbers',
        'expiredPermitsCondoInputType',
        'expiredPermitsCondoFreeText',
        'buildingViolationsUnit',
        'buildingViolationsCondo',
        'buildingViolationsUnitNumbers',
        'buildingViolationsCondoNumbers',
        'buildingViolationsCondoInputType',
        'buildingViolationsCondoFreeText'
      ],
      codeIssues: [
        'pendingViolationsUnit',
        'pendingViolationsCondo',
        'pendingViolationsUnitCases',
        'pendingViolationsCondoCases',
        'pendingViolationsCondoInputType',
        'pendingViolationsCondoFreeText',
        'openInvoicesUnit',
        'openInvoicesCondo',
        'openInvoicesUnitCases',
        'openInvoicesCondoCases'
      ],
      specialAssessmentsLiens: [
        'liensUnit',
        'liensCondo',
        'liensUnitNumbers',
        'liensCondoNumbers',
        'liensCondoInputType',
        'liensCondoFreeText'
      ]
    };

    // Clean up fields in taxes section
    if (reportData.taxes) {
      if (reportData.taxes.tangibleTax !== 'Yes') {
        delete reportData.taxes.tangibleTaxStatus;
        delete reportData.taxes.tangibleTaxAmountDue;
        delete reportData.taxes.tangibleTaxDetails;
        delete reportData.taxes.tangibleDelinquentTax;
        delete reportData.taxes.tangibleDelinquentTaxDetails;
        delete reportData.taxes.tangibleTaxNotes;
      } else {
        pruneEmptyTaxAccountRows(reportData.taxes, 'tangible');
        if (reportData.taxes.tangibleDelinquentTax !== 'Yes') {
          delete reportData.taxes.tangibleDelinquentTaxDetails;
        }
      }

      if (reportData.taxes.businessTax !== 'Yes') {
        delete reportData.taxes.businessTaxStatus;
        delete reportData.taxes.businessTaxAmountDue;
        delete reportData.taxes.businessTaxDetails;
        delete reportData.taxes.businessDelinquentTax;
        delete reportData.taxes.businessDelinquentTaxDetails;
        delete reportData.taxes.businessTaxNotes;
      } else {
        pruneEmptyTaxAccountRows(reportData.taxes, 'business');
        if (reportData.taxes.businessDelinquentTax !== 'Yes') {
          delete reportData.taxes.businessDelinquentTaxDetails;
        }
      }
    }

    Object.entries(cleanupFields).forEach(([section, fields]) => {
      if (reportData[section]) {
        fields.forEach(field => delete reportData[section][field]);
      }
    });

    // Remove documents from report data and map files back to sections
    if (reportData.documents) {
      const sections = ['invoices', 'taxes', 'permits', 'codeIssues', 'specialAssessmentsLiens', 'utilities'];
      sections.forEach(section => {
        reportData[section] = {
          ...reportData[section],
          files: reportData.documents[`${section}Files`] || []
        };
      });
      reportData.isDocumentSectionComplete = !!reportData.documents.isComplete;
      delete reportData.documents;
    }

    delete reportData.aiResponses;
    delete reportData.autoOrganizationRunning;
    delete reportData.autoOrganizationStartedAt;
    // Highlights are persisted separately via ReportSidebarFooter; never send form-polluted {}.
    delete reportData.pdfHighlights;
    delete reportData.generatedFiles;
    delete reportData._id;
    delete reportData.createdAt;
    delete reportData.updatedAt;
    delete reportData.__v;
    delete reportData.createdBy;
    delete reportData.updatedBy;

    const toCurrencyString = value => {
      if (value === '' || value == null) return '';
      const num = Number(value);
      return Number.isNaN(num) ? '' : String(roundMoney(num));
    };
    const setCurrency = (obj, key) => {
      if (obj && key in obj) obj[key] = toCurrencyString(obj[key]);
    };

    if (reportData.taxes) {
      ['grossAmount', 'totalAmountDue', 'tangibleTaxAmountDue', 'businessTaxAmountDue'].forEach(key =>
        setCurrency(reportData.taxes, key)
      );
      (reportData.taxes.tangibleTaxDetails || []).forEach(item => setCurrency(item, 'tangibleTaxBalance'));
      (reportData.taxes.tangibleDelinquentTaxDetails || []).forEach(item =>
        setCurrency(item, 'tangibleDelinquentTaxBalance')
      );
      (reportData.taxes.businessTaxDetails || []).forEach(item => setCurrency(item, 'businessTaxBalance'));
      (reportData.taxes.businessDelinquentTaxDetails || []).forEach(item =>
        setCurrency(item, 'businessDelinquentTaxBalance')
      );
    }
    if (reportData.invoices) {
      reportData.invoices.suggestedInvoiceAmount = computeSuggestedInvoiceAmount(
        reportData.invoices.hardCost,
        reportData.invoices.lienSearchProcessingFee,
        reportData.invoices.minimumInvoicableAmount
      );
      ['hardCost', 'lienSearchProcessingFee', 'minimumInvoicableAmount', 'suggestedInvoiceAmount'].forEach(key =>
        setCurrency(reportData.invoices, key)
      );
      reportData.invoices.invoices = (reportData.invoices.invoices || []).map(inv => {
        const mapped = {
          ...(inv._id != null && { _id: inv._id }),
          ...(inv.serviceVersionId != null && { serviceVersionId: inv.serviceVersionId }),
          invoiceNumber: inv.invoiceNumber || '',
          invoiceAmount: inv.invoiceAmount,
          paymentReceived: inv.paymentReceived,
          invoiceDueDate: inv.invoiceDueDate || undefined,
          status: inv.status || 'Unpaid'
        };
        setCurrency(mapped, 'invoiceAmount');
        setCurrency(mapped, 'paymentReceived');
        return mapped;
      });
    }
    if (reportData.codeIssues) {
      [
        ...(reportData.codeIssues.pendingViolationsUnitCases || []),
        ...(reportData.codeIssues.pendingViolationsCondoCases || [])
      ].forEach(item => setCurrency(item, 'due'));
      (reportData.codeIssues.openInvoicesCases || []).forEach(item => setCurrency(item, 'amount'));
    }

    return reportData;
  };

  // Use report generation hook
  const {
    isSaving,
    isGeneratingReport,
    reportGenerationMode,
    generatedFile,
    onSaveReport,
    onGenerateReport,
    onQuickPreview,
    onRefreshFile,
    handleHideGeneratedFile
  } = useReportGeneration({
    endpoints: {
      save: ENDPOINTS.REPORT_WITH_ID,
      generate: ENDPOINTS.GENERATE_REPORT
    },
    getEntityId: () => lienSearch?._id,
    getReportId: () => lienSearch?.reportId,
    prepareReportData,
    fileInformation,
    onSuccess: onReportGenerationSuccess,
    downloadFilePrefix: 'Lien_Search_Report',
    getApplyWaterMarkFlag: () => {
      const serviceRoot = service || lienSearch?.[0] || lienSearch;
      const lienOrder = findOrderOnService(serviceRoot, lienSearch?.report?.orderId, 'Lien Search');
      return lienOrder?.orderType === 'Lien Search' && lienOrder?.status === 'Preliminary Report';
    }
  });

  const isFilePopupOpen = generatedFile ? true : false;
  const isQaSidebarOpen = qaAnalysisMeta?.show;
  const isLayoutConstrained = isFilePopupOpen || isQaSidebarOpen;
  const formSteps = useMemo(() => getFormSteps(isLayoutConstrained, isWorkflowVisible), [
    isLayoutConstrained,
    isWorkflowVisible
  ]);

  const reportOrderId = useMemo(() => resolveReportOrderId(lienSearch?.report?.orderId), [lienSearch?.report?.orderId]);

  const utilitiesTaggedNotes = useMemo(() => {
    const lienOrder = findOrderOnService(service, lienSearch?.report?.orderId, 'Lien Search');
    const noteTime = note => {
      const time = new Date(note?.timestamp).getTime();
      return Number.isNaN(time) ? 0 : time;
    };
    return [...(service?.notes || []), ...(lienOrder?.notes || [])]
      .filter(note => noteHasUtilitiesTag(note?.text))
      .sort((a, b) => noteTime(b) - noteTime(a));
  }, [service, lienSearch?.report?.orderId]);

  const closeWorkflowPanel = useCallback(() => {
    setIsWorkflowVisible(false);
    setWorkflowMeta({ show: false });
  }, []);

  const closeQaPanel = useCallback(() => {
    setQaAnalysisMeta(prev => ({ ...prev, show: false }));
  }, []);

  const openQaPanel = useCallback(() => {
    closeWorkflowPanel();
    setQaAnalysisMeta(prev => ({ ...prev, show: true }));
  }, [closeWorkflowPanel]);

  const toggleWorkflowPanel = useCallback(() => {
    setIsWorkflowVisible(prev => {
      const willOpen = !prev;
      if (willOpen) {
        setQaAnalysisMeta(qaPrev => ({ ...qaPrev, show: false }));
        setWorkflowMeta({ show: true });
      } else {
        setWorkflowMeta({ show: false });
      }
      return willOpen;
    });
  }, []);

  useEffect(() => {
    if (!show || !reportOrderId) {
      setQaSections(createEmptySections());
      setQaFieldIssues({});
      setQaDocumentId(null);
      return;
    }

    let cancelled = false;

    const loadQaIssues = async () => {
      const { sections, qaDocumentId: docId, error } = await fetchReportQaSectionsByOrder(reportOrderId);
      if (cancelled) return;

      if (error) {
        toast.error(error);
        return;
      }

      setQaSections(sections);
      setQaDocumentId(docId);
      setQaFieldIssues(buildQaFieldIssueMap(sections));
    };

    loadQaIssues();

    return () => {
      cancelled = true;
    };
  }, [show, reportOrderId]);

  const handleQaIssuesChange = useCallback(sections => {
    setQaSections(sections);
    setQaFieldIssues(buildQaFieldIssueMap(sections));
  }, []);

  const fieldStatuses = useMemo(() => buildFieldStatusMapFromQaIssues(qaFieldIssues), [qaFieldIssues]);
  const hasQaFieldStatuses = Object.keys(fieldStatuses).length > 0;
  const qaUnresolvedStepKeys = useMemo(() => buildUnresolvedQaIssueStepKeys(qaSections), [qaSections]);

  const handleFormRefresh = useCallback(() => {
    if (service?.driveFolder) loadDriveFiles(service.driveFolder);
  }, [service?.driveFolder]);

  const handleReportFieldValueChange = useCallback(
    ({ field: sectionKey, key, value, formValues: fv, setFormValues: setFv, setConfirmationModal: setModal }) => {
      if (key === 'invoicesFiles') {
        autoFillInvoicesFromFiles({
          reportPropertyAddress: fileInformation.propertyAddress,
          value,
          formValues: fv,
          setFormValues: setFv,
          reportType: 'Lien Search'
        });
        return true;
      }

      if (key === 'taxesFiles') {
        autoFillTaxUtilitiesFromFiles({
          reportPropertyAddress: fileInformation.propertyAddress,
          value,
          formValues: fv,
          setFormValues: setFv,
          orderId: lienSearch?.report?.orderId,
          reportId: lienSearch?.reportId
        });
        return true;
      }

      if (sectionKey === 'taxes' && key === 'propertyTaxStatus' && value === 'Paid') {
        const taxes = fv.taxes || {};
        const grossAmount = Number(taxes.grossAmount) || 0;
        const totalAmountDue = Number(taxes.totalAmountDue) || 0;

        if (grossAmount > 0 && totalAmountDue === 0) {
          setModal({
            title: 'Confirm Paid Status',
            alertText: "This record shows a Gross Amount but no Total Amount Due. Are you sure it's paid?",
            continueButtonText: 'Yes, mark Paid',
            dismissButtonText: 'No, keep Unpaid',
            onContinue: () => {
              const finalFormValues = { ...fv };
              if (!finalFormValues[sectionKey]) finalFormValues[sectionKey] = {};
              finalFormValues[sectionKey][key] = value;
              setFv(finalFormValues);
              setModal(null);
            }
          });
          return true;
        }
      }

      if (
        sectionKey === 'invoices' &&
        ['hardCost', 'lienSearchProcessingFee', 'minimumInvoicableAmount'].includes(key)
      ) {
        const newFormValues = { ...fv };
        if (!newFormValues[sectionKey]) newFormValues[sectionKey] = {};
        newFormValues[sectionKey][key] = value;

        const invoicesSection = newFormValues[sectionKey];
        const hardCost = Number(invoicesSection.hardCost) || 0;
        const processingFee = Number(invoicesSection.lienSearchProcessingFee) || 0;
        const minimumInvoicableAmount = Number(invoicesSection.minimumInvoicableAmount) || 0;

        invoicesSection.suggestedInvoiceAmount = computeSuggestedInvoiceAmount(
          hardCost,
          processingFee,
          minimumInvoicableAmount
        );

        setFv(newFormValues);
        return true;
      }

      if (
        sectionKey === 'taxes' &&
        (key === 'propertyDelinquentTax' || key === 'grossAmount' || key === 'totalAmountDue')
      ) {
        const newFormValues = { ...fv };
        if (!newFormValues[sectionKey]) newFormValues[sectionKey] = {};
        newFormValues[sectionKey][key] = value;
        const taxes = newFormValues[sectionKey];
        if (taxes.propertyDelinquentTax === 'Yes' || (taxes.grossAmount > 0 && taxes.totalAmountDue > 0)) {
          newFormValues[sectionKey].propertyTaxStatus = 'Unpaid';
        }
        setFv(newFormValues);
        return true;
      }

      // Seed account rows when enabling tax sections; recalc amount due when delinquent toggles
      const taxDetailKey = TAX_DETAIL_KEYS_BY_RADIO[key];
      if (sectionKey === 'taxes' && taxDetailKey && (value === 'Yes' || value === 'No')) {
        const taxes = { ...(fv.taxes || {}), [key]: value };
        if (value === 'Yes' && !taxes[taxDetailKey]?.length) {
          taxes[taxDetailKey] = defaultValues.taxes[taxDetailKey].map(row => ({ ...row }));
        }
        if (key === 'tangibleDelinquentTax' || key === 'businessDelinquentTax') {
          const kind = key.startsWith('tangible') ? 'tangible' : 'business';
          taxes[`${kind}TaxAmountDue`] = String(computeTaxSectionAmountDue(taxes, kind));
        }
        setFv({ ...fv, taxes });
        return true;
      }

      return false;
    },
    [fileInformation.propertyAddress, lienSearch?.report?.orderId]
  );

  const handleQaIssueClick = useCallback(
    (issue, sectionKey) => {
      setActiveQaIssueId(issue.id);

      const navigation = resolveQaIssueNavigation({
        sectionKey,
        fieldNames: issue.fieldNames || []
      });
      if (!navigation) return;

      const { stepKey, fieldKey } = navigation;
      if (!stepKey) {
        scrollToFieldKey(fieldKey);
        return;
      }

      const index = formSteps.findIndex(s => s.key === stepKey);
      if (index !== -1) {
        setStep(index);
        setPendingQaScrollField(fieldKey);
      }
    },
    [formSteps]
  );

  useEffect(() => {
    if (!pendingQaScrollField) return;
    scrollToFieldKey(pendingQaScrollField);
    setPendingQaScrollField(null);
  }, [step, pendingQaScrollField]);

  useEffect(() => {
    if (!show) {
      handleHideGeneratedFile();
      setDriveFiles(null); // Clear cached files when sidebar closes
      setInitialFormState(null);
      closeWorkflowPanel();
      closeQaPanel();
      return;
    }

    if (openQa === 'true') {
      openQaPanel();
    }

    // Load drive files when sidebar opens
    if (show && service?.driveFolder && !driveFiles && !driveFilesLoading) {
      loadDriveFiles(service.driveFolder);
    }
  }, [show, openQa, service?.driveFolder, handleHideGeneratedFile, closeWorkflowPanel, closeQaPanel, openQaPanel]);

  const onHistoryReportChange = newReports => {
    setHistoryReport([...newReports]);
  };

  useEffect(() => {
    setHistoryReport(lienSearch?.report?.generatedFiles || []);
  }, [lienSearch?.report?.generatedFiles]);

  const fetchAndUpdateMunicipalityContacts = async () => {
    setMunicipalityMeta(meta => ({ ...meta, showProgress: true }));
    const { municipality } = municipalityMeta;

    try {
      // Make both API calls simultaneously
      const [cityResponse, countyResponse] = await Promise.all([
        makeRESTApiRequest({
          endpoint: ENDPOINTS.CITY_DEPARTMENT_CONTACTS_SEARCH,
          method: 'POST',
          requestBody: { municipality }
        }),
        makeRESTApiRequest({
          endpoint: ENDPOINTS.COUNTY_DEPARTMENT_CONTACTS_SEARCH,
          method: 'POST',
          requestBody: { municipality }
        })
      ]);

      // Check for errors
      if (cityResponse.error) {
        toast.error(cityResponse.error);
        return;
      }

      if (countyResponse.error) {
        toast.error(countyResponse.error);
        return;
      }

      // Process results
      const cityResults = cityResponse.response.results;
      const countyResults = countyResponse.response.results;

      const formattedBuildingContactEmails = getFormattedContact(cityResults, 'building');
      const formattedCodeContactEmails = getFormattedContact(cityResults, 'code');
      const formattedCountyBuildingContactEmails = getFormattedContact(countyResults, 'building');
      const formattedCountyCodeContactEmails = getFormattedContact(countyResults, 'code');

      // Update state
      setFileInformation({
        ...fileInformation,
        municipality: municipalityMeta?.municipality
      });
      setFormValues({
        ...formValues,
        permits: {
          ...(formValues?.permits || {}),
          permitContactEmail: formattedBuildingContactEmails.emails,
          permitContactNumber: formattedBuildingContactEmails.numbers,
          permitCountyContactEmail: formattedCountyBuildingContactEmails.emails,
          permitCountyContactNumber: formattedCountyBuildingContactEmails.numbers,
          cityContactInfo: {
            city: formattedBuildingContactEmails?.city,
            state: formattedBuildingContactEmails?.state
          },
          countyContactInfo: {
            city: formattedCountyBuildingContactEmails?.city,
            state: formattedCountyBuildingContactEmails?.state
          }
        },
        codeIssues: {
          ...(formValues?.codeIssues || {}),
          violationContactEmail: formattedCodeContactEmails.emails,
          violationContactNumber: formattedCodeContactEmails.numbers,
          violationCountyContactEmail: formattedCountyCodeContactEmails.emails,
          violationCountyContactNumber: formattedCountyCodeContactEmails.numbers,
          cityContactInfo: {
            city: formattedCodeContactEmails?.city,
            state: formattedCodeContactEmails?.state
          },
          countyContactInfo: {
            city: formattedCountyCodeContactEmails?.city,
            state: formattedCountyCodeContactEmails?.state
          }
        }
      });
    } catch (error) {
      toast.error('An error occurred while fetching municipality contacts');
    } finally {
      setMunicipalityMeta(meta => ({ ...meta, showProgress: false }));
      setMunicipalityMeta(null);
    }
  };

  const isWorkflowAllowed = useMemo(() => {
    return (
      REPORT_STEP_TO_INSTRUCTION_TAB[formSteps[step].key] && workflowMeta?.show && orderType === ORDER_TYPES.LIEN_SEARCH
    );
  }, [formSteps[step].key, workflowMeta?.show, service?.county, orderType]);

  const workflowService = useMemo(() => {
    if (!service) return service;
    return {
      ...service,
      // Keep WorkflowModal municipality in sync with confirmed municipality only.
      municipality: fileInformation?.municipality || service?.municipality
    };
  }, [service, fileInformation?.municipality]);

  // close workflow modal when file is generated
  useEffect(() => {
    if (generatedFile?.href) {
      closeWorkflowPanel();
    }
  }, [generatedFile?.href, closeWorkflowPanel]);

  return (
    <>
      <SlidingSideBar
        fullScreen
        visible={show}
        onClose={handleClose}
        title={`${lienSearch?.reportId ? 'Update' : 'Start'} Report`}
        additionalComponent={
          autoOrganizationRunning ? (
            <span
              className="d-inline-flex align-items-center px-2 py-1 text-muted border"
              style={{ borderRadius: 8, fontSize: 13, gap: 8, background: '#f4f4f5' }}
            >
              <Spinner animation="border" size="sm" />
              Organizing documents...
            </span>
          ) : null
        }
        contentStyle={{ position: 'relative' }}
        width={generatedFile ? '50%' : '100%'}
        style={generatedFile ? { background: 'transparent' } : undefined}
        dock="left"
      >
        {loading ? (
          <div className="d-flex justify-content-start align-items-center">
            <Loader />
          </div>
        ) : (
          <>
            <Card className="p-3 my-1 w-100">
              <div className="px-3">
                <Row className="p-0">
                  <Col className="p-0 pr-2" xs={isFilePopupOpen ? 6 : 3}>
                    <FieldStatusShell fieldKey="propertyAddress" statuses={fieldStatuses} label="Property Address">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="text"
                          value={fileInformation.propertyAddress}
                          onChange={e => setFileInformation({ ...fileInformation, propertyAddress: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className={`p-0 ${isFilePopupOpen ? '' : 'pr-2'}`} xs={isFilePopupOpen ? 6 : 3}>
                    <FieldStatusShell fieldKey="folio" statuses={fieldStatuses} label="Folio Number">
                      {styles => (
                        <FolioInputWithCopy
                          value={fileInformation.folio}
                          onChange={e => setFileInformation({ ...fileInformation, folio: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className="p-0 pr-2 pt-0 " xs={isFilePopupOpen ? 6 : 3}>
                    <FieldStatusShell fieldKey="municipality" statuses={fieldStatuses} label="Municipality">
                      {styles => (
                        <div
                          style={{
                            ...styles?.input,
                            ...(styles?.input?.borderColor && {
                              outline: `1px solid ${styles?.input?.borderColor}`,
                              borderRadius: '10px'
                            })
                          }}
                        >
                          <AdvancedSelect
                            items={municipalityOptions}
                            selectedItems={municipalityOptions.find(
                              option => option.value === fileInformation.municipality
                            )}
                            onChange={v => {
                              const value = v?.value || null;
                              const prevMunicipality = fileInformation?.municipality;

                              if (value && value !== prevMunicipality) {
                                setMunicipalityMeta({
                                  alert: true,
                                  municipality: value
                                });
                              } else {
                                setFileInformation({ ...fileInformation, municipality: value });
                              }
                            }}
                            placeholder="Select Municipality"
                            closeMenuOnSelect={true}
                            isMulti={false}
                            creatable={false}
                            searchable={false}
                            isClearable={true}
                            originalOptions={municipalityOptions}
                          />
                        </div>
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className="p-0" xs={isFilePopupOpen ? 6 : 3}>
                    <FieldStatusShell fieldKey="fileNo" statuses={fieldStatuses} label="Customer File">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="text"
                          value={fileInformation.fileNo}
                          onChange={e => setFileInformation({ ...fileInformation, fileNo: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                </Row>
              </div>
            </Card>
            <div className="p-0 mt-4 mb-5 d-flex flex-row gap-2 align-items-stretch w-100">
              <div
                className="flex-grow-1"
                style={{
                  minWidth: 0,
                  flex: isQaSidebarOpen ? '1 1 70%' : '1 1 auto',
                  maxWidth: isQaSidebarOpen ? '70%' : '100%'
                }}
              >
                <div className="d-flex justify-content-between align-items-center hide-scrollbar">
                  <StepTabNavigation
                    formSteps={formSteps}
                    currentStep={step}
                    formValues={formValues}
                    onTabSelect={onTabSelect}
                    onToggleStepCompletion={handleToggleStepCompletion}
                    onQaAnalysisClick={openQaPanel}
                    qaAnalysisMeta={qaAnalysisMeta}
                    qaUnresolvedStepKeys={qaUnresolvedStepKeys}
                  />
                </div>
                <hr className="m-0" />

                <div className="d-flex gap-0 w-100" style={{ minWidth: 0 }}>
                  <div
                    className="p-2 px-4 mt-2"
                    style={{
                      width: isWorkflowAllowed && !isFilePopupOpen ? '50%' : '100%',
                      minWidth: 0,
                      overflowX: 'auto'
                    }}
                  >
                    {show && (
                      <div className="position-relative">
                        {formSteps[step].key === 'utilities' && (
                          <UtilitiesProcessorNotes
                            notes={utilitiesTaggedNotes}
                            serviceId={service?._id}
                            reserveWorkflowToggle={
                              !isWorkflowVisible && Boolean(REPORT_STEP_TO_INSTRUCTION_TAB[formSteps[step].key])
                            }
                          />
                        )}
                        <ReportFormGenerator
                          orderType={ORDER_TYPES.LIEN_SEARCH}
                          defaultValues={defaultValues}
                          field={formSteps[step].key}
                          fields={formSteps[step].fields}
                          formValues={formValues}
                          setFormValues={setFormValues}
                          service={service}
                          isFilePopupOpen={isLayoutConstrained}
                          driveFiles={driveFiles}
                          driveFilesLoading={driveFilesLoading}
                          onUpdateDriveFiles={setDriveFiles}
                          setConfirmationModal={setConfirmationModal}
                          showValidationErrors={showValidationErrors}
                          setValidationStatus={setShowValidationErrors}
                          county={service?.county}
                          enableFieldStatus={hasQaFieldStatuses}
                          fieldStatuses={fieldStatuses}
                          handleRefresh={handleFormRefresh}
                          onFieldValueChange={handleReportFieldValueChange}
                          onMultiFieldChange={handleInvoiceMultiFieldChange}
                        />

                        {!isWorkflowVisible && REPORT_STEP_TO_INSTRUCTION_TAB[formSteps[step].key] && (
                          <div style={{ position: 'absolute', top: 15, right: 0 }}>
                            <Button
                              variant={workflowMeta?.show ? 'success' : 'outline-success'}
                              className="d-inline-flex align-items-center px-2 py-2"
                              style={{ borderRadius: '10px', gap: '8px' }}
                              onClick={toggleWorkflowPanel}
                            >
                              <FaListOl size={16} />
                              {workflowMeta?.show ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {isWorkflowAllowed && isWorkflowVisible && (
                    <div
                      className="p-2 mt-2"
                      style={{
                        width: '50%',
                        minWidth: 0,
                        flexShrink: 0
                      }}
                    >
                      <WorkflowModal
                        show={workflowMeta?.show}
                        onClose={closeWorkflowPanel}
                        service={workflowService}
                        rowOrderId={lienSearch?.report?.orderId}
                        // municipality for unsaved report
                        reportMunicipality={fileInformation?.municipality}
                        handleServiceUpdate={() => {}}
                        renderInline
                        instructionTab={REPORT_STEP_TO_INSTRUCTION_TAB[formSteps[step].key]}
                        handleReportUpdate={updatedReport => {
                          setFormValues(prev => ({ ...prev, ...updatedReport }));
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              {qaAnalysisMeta?.show && (
                <QaAnalysisSideBar
                  orderId={lienSearch?.report?.orderId}
                  qaDocumentId={qaDocumentId}
                  onQaDocumentIdChange={setQaDocumentId}
                  sections={qaSections}
                  onClose={closeQaPanel}
                  onIssueClick={handleQaIssueClick}
                  onIssuesChange={handleQaIssuesChange}
                  activeIssueId={activeQaIssueId}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onSaveReport={onSaveReport}
                  isSavingReport={isSaving}
                />
              )}
            </div>

            <ReportActionBar
              onPreviewHistory={onPreviewHistory}
              onSaveReport={onSaveReport}
              onGenerateReport={onGenerateReport}
              onQuickPreview={onQuickPreview}
              isGeneratingReport={isGeneratingReport}
              isSaving={isSaving}
              reportGenerationMode={reportGenerationMode}
            />

            <ReportSidebarFooter
              historyReport={historyReport}
              showHistory={showHistory}
              onCloseHistory={onCloseHistory}
              handleHideGeneratedFile={handleHideGeneratedFile}
              fileShareMetadata={fileShareMetadata}
              setFileShareMetadata={setFileShareMetadata}
              service={service}
              report={lienSearch?.report}
              reportId={lienSearch?.report?._id}
              onHistoryReportChange={onHistoryReportChange}
              shareEndpoint={ENDPOINTS.SHARE_REPORT}
              saveEndpoint={ENDPOINTS.REPORT_WITH_ID}
              generatedFile={generatedFile}
              reportGenerationMode={reportGenerationMode}
              isGeneratingReport={isGeneratingReport}
              onFileShareClick={onFileShareClick}
              onRefreshFile={onRefreshFile}
              showIsPreliminaryCheckbox={true}
              onShareComplete={onAfterSave}
            />
          </>
        )}

        <AlertModal
          show={municipalityMeta && municipalityMeta?.alert}
          onHide={() => setMunicipalityMeta(null)}
          onDismissClick={() => {
            setFileInformation({ ...fileInformation, municipality: municipalityMeta?.municipality });
            setMunicipalityMeta(null);
          }}
          onContinueClick={() => {
            fetchAndUpdateMunicipalityContacts();
          }}
          alertText="You are changing the municipality, Would you also like to update the associated contacts?"
          dismissButtonText="No"
          continueButtonText="Yes"
          continueButtonVariant="success"
          showProgress={municipalityMeta?.showProgress}
        />

        <ReportModals
          confirmationModal={confirmationModal}
          setConfirmationModal={setConfirmationModal}
          showUnsavedChangesAlert={showUnsavedChangesAlert}
          handleCancelAction={handleCancelAction}
          handleDiscardChanges={handleDiscardChanges}
          handleSaveAndContinue={handleSaveAndContinue}
          isSaving={isSaving}
        />
      </SlidingSideBar>
    </>
  );
};

export default AddEditReportSideBar;
